'use client';

import { FeedbackThumbIconButton } from '@app/(main)/(dark)/ai/_shared/_components/Feedback/FeedbackThumbIconButton';
import sendAiContentFeedbackEvent from 'components/components/events/AIContentFeedbackEvent';
import { useCallback, useState, type JSX } from 'react';

import { Button, type ButtonProps, Text, TextAreaInput } from '@videoblocks/design-system';
import { AiContentFeedbackScore } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentFeedbackScoreEnum';
import { AiContentType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentTypeEnum';
import { DownloadBottom } from '@videoblocks/react-icons';

import { VIDEO_GENERATION_MODEL_OPTIONS } from '../_context/constants';
import { AiToolkitCategory } from '../_context/types';

const FEEDBACK_MAX_LENGTH = 1000;

async function downloadFile(url: string, filename: string): Promise<void> {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(blobUrl);
}

export type PostGenerationActionSectionProps = {
    downloadUrl: string;
    downloadFilename: string;
    downloadButtonText?: string;
    onDownload: () => void;
    category: AiToolkitCategory;
    contentId?: string | null;
    aiContentId: string;
    lastAiContentId: string | null;
    aiContentType: AiContentType;
    aiModelName?: string;
    onSecondaryButtonClick?: () => void;
    secondaryButtonText?: string;
    secondaryButtonProps?: Omit<ButtonProps, 'onClick' | 'children'>;
};

export const PostGenerationActionSection = ({
    downloadUrl,
    downloadFilename,
    downloadButtonText = 'Download',
    onDownload,
    contentId = null,
    aiContentId,
    lastAiContentId = null,
    aiContentType,
    aiModelName = VIDEO_GENERATION_MODEL_OPTIONS[0].value,
    onSecondaryButtonClick,
    secondaryButtonText,
    secondaryButtonProps,
}: PostGenerationActionSectionProps): JSX.Element => {
    const [feedback, setFeedback] = useState<'approve' | 'disapprove' | null>(null);
    const [feedbackContent, setFeedbackContent] = useState('');
    const [showPostSubmitMessage, setShowPostSubmitMessage] = useState(false);
    const isApproveSelected = feedback === 'approve';
    const isDisapproveSelected = feedback === 'disapprove';
    const handleSubmitFeedback = useCallback(() => {
        if (typeof window !== 'undefined') {
            void sendAiContentFeedbackEvent({
                aiContentId,
                voiceId: null,
                aiContentVoiceoverType: null,
                currentUrl: window.location.href,
                referrer: document.referrer || undefined,
                contentId,
                lastAiContentId,
                aiModelName,
                aiContentType,
                aiContentFeedbackMessage: feedbackContent,
                aiContentFeedbackScore:
                    feedback === 'approve' ? AiContentFeedbackScore.HAPPY_FACE : AiContentFeedbackScore.SAD_FACE,
            });
        }
    }, [feedbackContent, feedback, contentId, aiContentId, lastAiContentId, aiContentType, aiModelName]);

    return (
        <div className="w-full flex flex-col gap-6">
            <div className="flex flex-col-reverse gap-4 lg:flex-row justify-between lg:gap-2">
                <div className="flex gap-1 self-center">
                    <FeedbackThumbIconButton
                        isApprove={true}
                        isSelected={isApproveSelected}
                        onClick={() => {
                            if (showPostSubmitMessage) return;
                            setFeedback('approve');
                        }}
                        disabled={showPostSubmitMessage}
                    />
                    <FeedbackThumbIconButton
                        isApprove={false}
                        isSelected={isDisapproveSelected}
                        onClick={() => {
                            if (showPostSubmitMessage) return;
                            setFeedback('disapprove');
                        }}
                        disabled={showPostSubmitMessage}
                    />
                </div>
                <div className="flex flex-col-reverse lg:flex-row gap-3">
                    {onSecondaryButtonClick && secondaryButtonText && (
                        <Button variant="outlined-light" {...secondaryButtonProps} onClick={onSecondaryButtonClick}>
                            {secondaryButtonText}
                        </Button>
                    )}
                    <Button
                        iconPrefix={<DownloadBottom />}
                        onClick={() => {
                            onDownload();
                            void downloadFile(downloadUrl, downloadFilename);
                        }}
                    >
                        {downloadButtonText}
                    </Button>
                </div>
            </div>
            {feedback !== null && !showPostSubmitMessage && (
                <div className="flex flex-col gap-4">
                    <TextAreaInput
                        placeholder="Share your feedback"
                        value={feedbackContent}
                        onChange={(e) => {
                            setFeedbackContent(e.target.value);
                        }}
                        maxLength={FEEDBACK_MAX_LENGTH}
                        showCount
                        resize="none"
                        className="border-[1.5px] dark:border-neutral-dark-300"
                        textAreaClassName="field-sizing-fixed min-h-35"
                    />
                    <Button
                        variant="outlined-light"
                        className="self-end"
                        onClick={() => {
                            setShowPostSubmitMessage(true);
                            handleSubmitFeedback();
                        }}
                    >
                        Submit Feedback
                    </Button>
                </div>
            )}
            {showPostSubmitMessage && (
                <div className="flex flex-col gap-4">
                    <Text fontSize="sm" fontWeight="regular">
                        Thanks for your feedback!
                    </Text>
                </div>
            )}
        </div>
    );
};
